import { useState } from "react";
import { Bell, BellRing, Monitor } from "lucide-react";
import { useAlerts } from "@/contexts/AlertsContext";

const rel = (ts) => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};
const dot = (sev) => (sev === "high" ? "bg-red-500" : sev === "med" ? "bg-amber-500" : "bg-zinc-500");

export default function AlertsBell() {
  const { alerts, unread, markAllRead, clearAll, desktop, toggleDesktop } = useAlerts();
  const [open, setOpen] = useState(false);

  const onToggle = () => {
    setOpen((o) => {
      const n = !o;
      if (n && unread) markAllRead();
      return n;
    });
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="alerts-bell"
        onClick={onToggle}
        className="relative flex items-center gap-1.5 text-zinc-500 hover:text-amber-term transition-colors"
        title="Alerts"
      >
        {unread > 0 ? (
          <BellRing size={13} strokeWidth={1.5} className="text-amber-term" />
        ) : (
          <Bell size={13} strokeWidth={1.5} />
        )}
        {unread > 0 && (
          <span
            data-testid="alerts-badge"
            className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-1 rounded-full bg-red-500 text-white text-[11px] leading-[14px] text-center font-semibold"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            data-testid="alerts-panel"
            className="absolute right-0 mt-2 w-80 max-h-[440px] overflow-auto border border-zinc-700 bg-[#09090B] z-40 shadow-2xl"
          >
            <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-[#09090B]">
              <span className="text-[11px] uppercase tracking-widest text-zinc-400">Alerts</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleDesktop}
                  data-testid="alerts-desktop-toggle"
                  title="Toggle desktop notifications"
                  className={`flex items-center gap-1 text-[11px] uppercase tracking-widest ${
                    desktop ? "text-pos" : "text-zinc-600"
                  } hover:text-amber-term transition-colors`}
                >
                  <Monitor size={11} strokeWidth={1.5} /> {desktop ? "ON" : "OFF"}
                </button>
                {alerts.length > 0 && (
                  <button
                    onClick={clearAll}
                    data-testid="alerts-clear"
                    className="text-[11px] uppercase tracking-widest text-zinc-600 hover:text-neg transition-colors"
                  >
                    clear
                  </button>
                )}
              </div>
            </div>

            {alerts.length === 0 ? (
              <div className="px-3 py-10 text-center text-[11px] uppercase tracking-widest text-zinc-600">
                No alerts yet · market synopsis, flash news &amp; journal target/stop hits
              </div>
            ) : (
              alerts.map((a) => (
                <div key={a.id} className="px-3 py-2 border-b border-zinc-900 flex gap-2" data-testid="alert-item">
                  <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${dot(a.severity)}`} />
                  <div className="min-w-0">
                    <div className="text-[11px] text-zinc-100 leading-snug">{a.title}</div>
                    {a.link ? (
                      <a href={a.link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-500 hover:text-amber-term leading-snug transition-colors">
                        {a.body}
                      </a>
                    ) : (
                      <div className="text-[11px] text-zinc-500 leading-snug">{a.body}</div>
                    )}
                    <div className="text-[11px] uppercase tracking-widest text-zinc-600 mt-0.5">{rel(a.ts)} ago</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
